import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Headphones, Landmark, Megaphone, MessageCircle, Shield,
  ShoppingCart, Tag, Truck, Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const WHATSAPP_NUMBER = (process.env.REACT_APP_WHATSAPP_NUMBER || '').replace(/[^\d]/g, '');

const FUNCTION_ICONS = {
  megaphone: Megaphone,
  tag: Tag,
  'shopping-cart': ShoppingCart,
  headphones: Headphones,
  truck: Truck,
  landmark: Landmark,
  shield: Shield,
};

/**
 * Business OS — the merchant's company org chart, staffed by agents.
 * Functions → sub-functions → agents, with honest live/planned status.
 */
export default function BusinessOSPage() {
  const [functions, setFunctions] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('ci_session_token')
      || sessionStorage.getItem('session_token');
    fetch(`${BACKEND_URL}/api/business-os/functions`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then(data => setFunctions(data.functions || []))
      .catch(() => setError('Sign in to see your Business OS.'));
  }, []);

  const waLink = (fnName) => WHATSAPP_NUMBER
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Talk to ${fnName}`)}`
    : null;

  return (
    <div className="min-h-screen bg-[#0B1120] text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      <nav className="border-b border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#3EF1C7] flex items-center justify-center">
              <span className="text-[#0B1120] font-bold text-sm">CI</span>
            </div>
            <span className="font-semibold">Business OS</span>
          </Link>
          <Link to="/dashboard" className="text-sm text-[#94A3B8] hover:text-white">Dashboard</Link>
        </div>
      </nav>

      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3EF1C7]/10 border border-[#3EF1C7]/20 mb-4">
          <Users className="w-4 h-4 text-[#3EF1C7]" />
          <span className="text-sm text-[#3EF1C7] font-medium">Your company, staffed by agents</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-3">
          Every function of your business. <span className="text-[#3EF1C7]">One OS.</span>
        </h1>
        <p className="text-sm text-[#94A3B8] max-w-2xl mx-auto">
          Sales, pricing, supply chain, finance — each department has agents that
          prepare, research, act, and report back over WhatsApp, email, or a call
          when it's urgent. Text "talk to supply chain" anytime.
        </p>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {error && <div className="text-center text-[#94A3B8] py-12">{error}</div>}
        {!functions && !error && (
          <div className="text-center text-[#64748B] py-12">Loading your org…</div>
        )}

        {functions && (
          <div className="grid md:grid-cols-2 gap-6">
            {functions.map(fn => {
              const Icon = FUNCTION_ICONS[fn.icon] || Users;
              const isOpen = expanded === fn.id;
              return (
                <div key={fn.id}
                     className="rounded-xl border border-[#22304A] bg-[#0D1628] p-6 hover:border-[#3EF1C7]/30 transition-colors">
                  <button className="w-full text-left" onClick={() => setExpanded(isOpen ? null : fn.id)}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#3EF1C7]/10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-[#3EF1C7]" />
                        </div>
                        <div>
                          <h2 className="font-semibold">{fn.name}</h2>
                          <div className="text-xs text-[#64748B]">
                            {fn.agents_live}/{fn.agents_total} agents live
                            {fn.decisions_30d > 0 && ` · ${fn.decisions_30d} decisions (30d)`}
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-[#94A3B8]">{fn.description}</p>
                  </button>

                  {isOpen && (
                    <div className="mt-4 space-y-3 border-t border-[#22304A] pt-4">
                      {fn.sub_functions.map(sub => (
                        <div key={sub.name}>
                          <div className="text-xs uppercase tracking-wider text-[#64748B] mb-1.5">
                            {sub.name}
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {sub.agents.map(a => (
                              <span key={a.agent_type}
                                    className={`text-[11px] px-2 py-0.5 rounded-full border ${
                                      a.status === 'live'
                                        ? 'border-[#3EF1C7]/30 text-[#3EF1C7] bg-[#3EF1C7]/5'
                                        : 'border-[#334155] text-[#64748B]'
                                    }`}>
                                {a.agent_type.replace(/_agent$/, '').replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                      {waLink(fn.name) && (
                        <a href={waLink(fn.name)} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1.5 mt-2 text-xs text-[#25D366] hover:underline">
                          <MessageCircle className="w-3.5 h-3.5" />
                          Message this department on WhatsApp
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="text-center text-xs text-[#64748B] mt-10">
          Grey chips are planned roles from the full org template — they activate
          as the platform grows. Mint chips are live agents working today.
        </p>
      </main>
    </div>
  );
}
